import { X } from 'lucide-react';

interface TagBadgeProps {
  name: string;
  color: string;
  onRemove?: () => void;
  onClick?: () => void;
  active?: boolean;
  className?: string;
}

/**
 * Small colored pill showing a tag name with its hex color as background.
 */
export function TagBadge({
  name,
  color,
  onRemove,
  onClick,
  active,
  className = '',
}: TagBadgeProps) {
  // Compute a contrasting text color (white for dark backgrounds, dark for light)
  const brightness = hexBrightness(color);
  const textColor = brightness > 160 ? '#1f2937' : '#ffffff';

  const baseClasses = `inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-opacity ${className}`;
  const activeClasses = active === false ? 'opacity-50' : '';
  const clickableClasses = onClick ? 'cursor-pointer hover:opacity-80' : '';

  return (
    <span
      className={`${baseClasses} ${activeClasses} ${clickableClasses}`}
      style={{ backgroundColor: color, color: textColor }}
      onClick={onClick}
    >
      {name}
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 rounded-full p-0.5 hover:bg-black/10"
          aria-label={`Remove ${name} tag`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

function hexBrightness(hex: string): number {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000;
}
